import { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";

/** REIN feed status, manual sync and the Matrix search link shown to visitors. */
export const FeedPanel = ({ meta, reload }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    setUrl(meta?.matrix_url || "");
  }, [meta?.matrix_url]);

  const run = async (kind, fn, ok) => {
    setBusy(kind);
    try {
      await fn();
      toast.success(ok);
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy("");
    }
  };
  const live = meta && !meta.sample;

  return (
    <section className="grid gap-4 lg:grid-cols-2" data-testid="feed-panel">
      <div className="rounded-md border border-neutral-200 bg-white p-5">
        <p className="eyebrow">REIN MLS feed</p>
        <p className="mt-2 flex items-center gap-2 text-lg text-ink" data-testid="feed-status">
          <span className={`h-2.5 w-2.5 rounded-full ${live ? "bg-emerald-500" : "bg-brass"}`} />
          {live ? `Live · ${meta.listing_count} REIN listings` : `Showing ${meta?.listing_count ?? 0} sample listings`}
        </p>
        <p className="mt-1 text-[14px] text-neutral-600">
          {meta?.rein_configured
            ? `Feed keys added.${meta.last_sync ? ` Last sync ${new Date(meta.last_sync).toLocaleString()}.` : ""}`
            : "Waiting for your Trestle (Cotality) Client ID and Secret. Once added, live listings replace the samples."}
        </p>
        <button disabled={!meta?.rein_configured || busy === "sync"} data-testid="feed-sync-button"
          onClick={() => run("sync", () => api.post("/admin/sync"), "REIN listings synced")}
          className="mt-4 inline-flex h-10 items-center gap-2 rounded-full bg-ink px-5 text-[14px] text-white transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-40">
          {busy === "sync" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync now
        </button>
      </div>
      <form className="rounded-md border border-neutral-200 bg-white p-5" data-testid="matrix-link-form"
        onSubmit={(e) => { e.preventDefault(); run("save", () => api.put("/admin/settings", { matrix_url: url }), url ? "Search link saved" : "Search link removed"); }}>
        <p className="eyebrow">Matrix search link</p>
        <p className="mt-2 text-[14px] text-neutral-600">
          Paste the link to one of your REIN Matrix IDX search pages. Visitors will see a button to search every REIN listing while the live feed is pending.
        </p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…matrix…" className="h-10" data-testid="matrix-link-input" />
          <button type="submit" disabled={busy === "save"} data-testid="matrix-link-save"
            className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-full border border-ink px-5 text-[14px] text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-50">
            {busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />} Save link
          </button>
        </div>
      </form>
    </section>
  );
};
